import { ReactNode } from 'react'

import BlobIcon from '@totallymoney/ui/components/BlobIcon'
import Box from '@totallymoney/ui/components/Box'
import Divider from '@totallymoney/ui/components/Divider'
import Flex from '@totallymoney/ui/components/Flex'
import Heading from '@totallymoney/ui/components/Heading'
import Modal from '@totallymoney/ui/components/Modal'
import Row from '@totallymoney/ui/components/Row'
import Stack from '@totallymoney/ui/components/Stack'
import Text from '@totallymoney/ui/components/Text'

import { LoanName } from '@appTypes/LoansApi.interface'

const StyledText = ({ children }: { children: ReactNode }) => (
    <Text color="light" variant="100">
        {children}
    </Text>
)

interface LoanTypeModalProps {
    isOceanSecureLoan?: boolean
    loanName?: string
    onModalClose: () => void
    showModal: boolean
}

const loanNameDescriptions: Record<string, ReactNode> = {
    [LoanName.CarFinanceBroker]: (
        <StyledText>
            This loan is arranged through a car finance broker, which is someone
            who helps you find and arrange your car finance loan. Brokers work
            with a variety of lenders to find the best finance option for you,
            as well as helping you find your car. Choosing a car finance broker
            on TotallyMoney means we’ll connect you to the broker.
        </StyledText>
    ),

    [LoanName.GuarantorLoan]: (
        <StyledText>
            This type of loan is where someone — a guarantor — agrees to cover
            your repayments if you can’t. Guarantor loans are good if you’re
            struggling to get a personal loan, as having a guarantor gives
            lenders confidence they’re likely to get their money back. A
            guarantor should be someone you trust who has a good credit history.
            Like other loans, there’s usually a fixed interest rate and you’ll
            pay it back over a set period.
        </StyledText>
    ),

    [LoanName.HirePurchase]: (
        <StyledText>
            This is a type of car finance loan. Hire Purchase (HP) agreements
            let you put down a deposit on a car, then pay off the rest in
            monthly instalments, plus interest, until 100% of the car is owned
            at the end of the term. You can apply for an HP agreement before
            going to the dealership, so you know how much you can borrow in
            advance. But, if you can’t keep up with the repayments, the finance
            company can take your car.
        </StyledText>
    ),

    [LoanName.PersonalLoan]: (
        <StyledText>
            Personal loans, sometimes called unsecured loans, let you borrow an
            agreed amount of money, which is paid directly into your bank
            account. You’ll repay it over a set amount of time in fixed monthly
            instalments, usually with a fixed interest rate. Interest rates vary
            depending on your credit history, and some personal loans have a fee
            if you pay them back early.
        </StyledText>
    ),

    [LoanName.SecuredLoan]: (
        <Stack space="space20">
            <StyledText>
                A homeowner loan is for those who own a home, either outright or
                with a mortgage.
            </StyledText>

            <StyledText>
                The loan is secured against your home, so you can often borrow a
                larger amount of money than an personal loan. Loans are
                typically between £15,000 and £100,000.
            </StyledText>

            <StyledText>
                If you’re eligible for a homeowner loan this will be shown with
                your other loans results.
            </StyledText>
        </Stack>
    ),
}

const LoanTypeModal = ({
    loanName,
    showModal,
    onModalClose,
    isOceanSecureLoan,
}: LoanTypeModalProps) => {
    return (
        <Modal
            title={isOceanSecureLoan ? 'What’s a homeowner loan?' : loanName}
            hasHeader={false}
            hasTooltipHeader={true}
            isOpen={showModal}
            onClose={onModalClose}
            isFullScreen={true}
            hasMinHeight={false}
        >
            {loanName != null ? (
                <>
                    {loanNameDescriptions[loanName] || null}

                    {isOceanSecureLoan ? (
                        <>
                            <Box>
                                <Divider color="neutral160" spacing="space20" />

                                <Row space="space20" mb="space20">
                                    <BlobIcon blobName="info" />

                                    <Flex alignItems="center">
                                        <Heading variant="40" as="h1">
                                            What’s an interest rate?
                                        </Heading>
                                    </Flex>
                                </Row>

                                <Stack space="space20">
                                    <StyledText>
                                        Your interest rate, set by the lender,
                                        represents what you’ll pay for the loan
                                        over a year. It’s shown as a percentage
                                        of the amount you’re borrowing.
                                    </StyledText>

                                    <StyledText>
                                        All details will be discussed with an
                                        advisor, before proceeding with your
                                        loan.
                                    </StyledText>
                                </Stack>

                                <Divider color="neutral160" spacing="space20" />

                                <Row space="space20" mb="space20">
                                    <BlobIcon blobName="info" />

                                    <Flex alignItems="center">
                                        <Heading variant="40" as="h1">
                                            What’s APRC?
                                        </Heading>
                                    </Flex>
                                </Row>

                                <Stack space="space20">
                                    <StyledText>
                                        APRC stands for Annual Percentage Rate
                                        of Charge. This shows the cost of a
                                        homeowner loan over its full length or
                                        lifetime, representing how different
                                        rates and charges could have an effect.
                                    </StyledText>

                                    <StyledText>
                                        APRC could help you work out the overall
                                        costs of different loans, from various
                                        lenders. For example, some homeowner
                                        loans could have an introductory or
                                        promotional rate, but after a certain
                                        period of time, may switch to a higher
                                        variable rate.
                                    </StyledText>
                                </Stack>
                            </Box>
                        </>
                    ) : null}
                </>
            ) : null}
        </Modal>
    )
}

export default LoanTypeModal
